import { Button, ListItemText, Typography } from '@mui/material';
import { ReactNode } from 'react';

import { StatusIcon } from '@/components/DataDisplay/StatusIcon';
import { ActionBar } from '@/components/Layout';
import { DesktopOnly, MobileOnly } from '@/components/Layout/Responsive';

import { useTranslate } from '@/hooks/useTranslate';

import { Option } from '@/types/components/common';

import { getDataTestId } from '@/utils/browser';

import { PreQuestionsContainer, QuestionItem } from './components.styled';

type PreQuestionsProps = Readonly<{
  question: string;
  options?: Option[];
  confirmationSection?: {
    description: string;
    onYes: () => void;
    onNo: () => void;
  };
  onSelect: (option: Option) => void;
  renderItemIcon?: (option: Option) => ReactNode;
  renderItemText?: (option: Option) => ReactNode;
  disableItem?: (option: Option) => boolean;
  colorTheme?: 'primary' | 'grey';
}>;

export function PreQuestions({
  question,
  confirmationSection,
  options = [],
  onSelect,
  renderItemIcon,
  renderItemText,
  colorTheme = 'primary',
  disableItem,
}: PreQuestionsProps) {
  const { t } = useTranslate();
  return (
    <>
      <MobileOnly>
        <Typography variant="h3" mb={3} textAlign="center">
          {question}
        </Typography>
      </MobileOnly>
      <DesktopOnly>
        <Typography variant="h3" textAlign="center">
          {question}
        </Typography>
      </DesktopOnly>
      <PreQuestionsContainer>
        {confirmationSection && (
          <>
            <Typography variant="body2" textAlign="center">
              {confirmationSection.description}
            </Typography>
            <ActionBar mt={3}>
              <Button
                {...getDataTestId(`basic-info-yes-button`)}
                variant="outlined"
                fullWidth
                onClick={confirmationSection.onYes}
              >
                {t('moreInfo.preQuestions.button.yes')}
              </Button>
              <Button
                {...getDataTestId(`basic-info-no-button`)}
                variant="outlined"
                fullWidth
                onClick={confirmationSection.onNo}
              >
                {t('moreInfo.preQuestions.button.no')}
              </Button>
            </ActionBar>
          </>
        )}
        {options.map(option => (
          <QuestionItem
            {...getDataTestId(`question-item-${option.value}`)}
            key={option.value}
            onClick={
              option.disabled || disableItem?.(option)
                ? undefined
                : () => onSelect(option)
            }
          >
            {renderItemIcon ? (
              renderItemIcon(option)
            ) : (
              <StatusIcon
                value=""
                iconName={option.icon}
                backgroundColor={theme => {
                  if (colorTheme === 'grey') return theme.palette.grey[100];
                  if (colorTheme === 'primary')
                    return theme.palette.primaries[100];
                  return undefined;
                }}
                color={theme => {
                  if (colorTheme === 'grey') return theme.palette.grey[700];
                  if (colorTheme === 'primary')
                    return theme.palette.primaries[400];
                  return undefined;
                }}
                size={40}
              />
            )}
            {renderItemText ? (
              renderItemText(option)
            ) : (
              <ListItemText>
                <Typography variant="body2">
                  {t(option.text || String(option.value))}
                </Typography>
              </ListItemText>
            )}
          </QuestionItem>
        ))}
      </PreQuestionsContainer>
    </>
  );
}
